import axios from "axios";
import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  CircularProgress
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { authAPI } from "services/apis.service";
import { validateUserPassword } from "utils/validation.util";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordShow, setPasswordShow] = useState(false);
  const [loading, setloading] = useState(false);

  async function onSubmitLogin(e) {
    e.preventDefault();
    if (loading) return;

    if (!validateUserPassword(password)) {
      toast.error("Please enter a valid password");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Password do not match");
      return;
    }

    try {
      setloading(true);

      const res = await axios.post(authAPI.resetPasswrd, {
        userId: searchParams.get("userId"),
        code: searchParams.get("code"),
        resetPassword: password,
        resetConfirmPassword: confirmPassword,
      });

      if (res.data) {
        toast.success(res.data.message);
        navigate("/");
      }
    } catch (error) {
      if (error?.response?.status === 400) {
        toast.error(
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          "Invalid or expired reset link. Please try again."
        );
      } else {
        console.error(error);
        toast.error(error?.response?.data?.title || "Something went wrong. Please try again.");
      }
    } finally {
      setloading(false);
    }
  }

  return (
    <Stack direction="column" gap={2}>
      <Typography variant="h3">Reset Password</Typography>
      <Box
        component="form"
        onSubmit={onSubmitLogin}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          px: 5,
        }}
      >
        <FormControl variant="outlined" size="small" disabled={loading}>
          <InputLabel htmlFor="password">New Password</InputLabel>
          <OutlinedInput
            required
            id="password"
            type={passwordShow ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setPasswordShow((value) => !value)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                  disabled={loading}
                >
                  {!passwordShow ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="New Password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            disabled={loading}
          />
          <FormHelperText id="password-helper-text">
            NOTE: Password should be minimum 8 characters with at least one
            uppercase letter, one lowercase letter, one special character and
            one number.
          </FormHelperText>
        </FormControl>
        <FormControl variant="outlined" size="small" disabled={loading}>
          <InputLabel htmlFor="confirm-password">
            Confirm New Password
          </InputLabel>
          <OutlinedInput
            required
            id="confirm-password"
            type={passwordShow ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setPasswordShow((value) => !value)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                  disabled={loading}
                >
                  {!passwordShow ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Confirm New Password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            value={confirmPassword}
            disabled={loading}
          />
        </FormControl>

        <Stack direction="row" gap={2} justifyContent="end">
          <Button variant="outlined" LinkComponent={Link} to="/" disabled={loading}>
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Sign Up
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
